/**
 * ShortLinksPanel — Studio header item: manage every story's short
 * promotional URL (storylo.co.uk/{slug}). Table with copy, inline slug
 * editing (conflict suggestions), QR download, one-click social share
 * and click analytics (referrer / device / country).
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  Link2, Loader2, X, Copy, Check, QrCode, Pencil,
  Facebook, Twitter, MessageCircle, Linkedin, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => {
  try { return window.localStorage.getItem("storylo_admin_token") || "storylo-admin"; }
  catch { return "storylo-admin"; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

const flag = (cc) =>
  /^[A-Z]{2}$/.test(cc) && cc !== "XX"
    ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : "🌐";

const topEntries = (obj, n = 4) =>
  Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).slice(0, n);

function ShareRow({ url, title }) {
  const enc = encodeURIComponent(url);
  const txt = encodeURIComponent(`${title} — ${url}`);
  const links = [
    { icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc}`, label: "Facebook" },
    { icon: Twitter, href: `https://twitter.com/intent/tweet?text=${txt}`, label: "X" },
    { icon: MessageCircle, href: `https://wa.me/?text=${txt}`, label: "WhatsApp" },
    { icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc}`, label: "LinkedIn" },
  ];
  return (
    <span className="inline-flex items-center gap-1">
      {links.map(({ icon: Icon, href, label }) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer"
           className="p-1.5 rounded hover:bg-white/10 text-[color:var(--muted)] hover:text-[color:var(--accent)]"
           title={`Share on ${label}`} data-testid={`shortlink-share-${label.toLowerCase()}`}>
          <Icon className="w-3.5 h-3.5" />
        </a>
      ))}
    </span>
  );
}

function Row({ row, base, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.slug);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const shortUrl = `https://${base}/${row.slug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success(`Copied ${base}/${row.slug}`);
    } catch { toast.error("Couldn't copy — select and copy manually"); }
  };

  const save = async (slugValue) => {
    const slug = (slugValue || value).trim().toLowerCase();
    if (!slug || slug === row.slug) { setEditing(false); return; }
    setSaving(true);
    setSuggestions([]);
    try {
      await axios.post(`${API}/shortlinks/${row.video_id}`, { slug },
        { headers: { "X-Admin-Token": token() } });
      toast.success(`Short link is now ${base}/${slug}`);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.suggestions) {
        setSuggestions(d.suggestions);
        toast.error(d.message || "That slug is taken");
      } else {
        toast.error(typeof d === "string" ? d : "Couldn't save slug");
      }
    } finally { setSaving(false); }
  };

  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/[.03]" data-testid={`shortlink-row-${row.video_id}`}>
        <td className="py-2.5 pr-3 max-w-[220px]">
          <div className="truncate text-sm" title={row.title}>{row.title}</div>
          <div className="text-[10px] text-[color:var(--muted)] truncate">{row.destination}</div>
        </td>
        <td className="py-2.5 pr-3">
          {editing ? (
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-[color:var(--muted)]">{base}/</span>
                <input
                  value={value}
                  onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                  className="bg-white/[.06] border border-white/15 rounded px-2 py-1 text-xs w-36 focus:outline-none focus:border-[color:var(--accent)]"
                  autoFocus
                  data-testid={`shortlink-edit-input-${row.video_id}`}
                />
                <button onClick={() => save()} disabled={saving}
                        className="px-2 py-1 rounded bg-[color:var(--accent)] text-black text-[10px] font-medium disabled:opacity-50"
                        data-testid={`shortlink-save-${row.video_id}`}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                </button>
                <button onClick={() => { setEditing(false); setSuggestions([]); }}
                        className="text-[color:var(--muted)] hover:text-white"><X className="w-3.5 h-3.5" /></button>
              </div>
              {suggestions.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`shortlink-suggestions-${row.video_id}`}>
                  <span className="text-[10px] text-amber-300">Try:</span>
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => { setValue(s); save(s); }}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-white/[.06] hover:bg-[color:var(--accent)]/20 text-[color:var(--accent)]">
                      /{s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-[color:var(--accent)] font-medium">{base}/{row.slug}</span>
              <button onClick={() => { setValue(row.slug); setEditing(true); }}
                      className="p-1 rounded hover:bg-white/10 text-[color:var(--muted)] hover:text-white"
                      title="Edit short link" data-testid={`shortlink-edit-${row.video_id}`}>
                <Pencil className="w-3 h-3" />
              </button>
            </div>
          )}
          {row.old_slugs?.length > 0 && (
            <div className="text-[10px] text-[color:var(--muted)] mt-0.5">
              also redirects: {row.old_slugs.map((s) => `/${s}`).join(", ")}
            </div>
          )}
        </td>
        <td className="py-2.5 pr-3 text-center">
          <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${row.published ? "text-emerald-300 bg-emerald-400/10" : "text-[color:var(--muted)] bg-white/[.05]"}`}>
            {row.published ? row.status : "draft"}
          </span>
        </td>
        <td className="py-2.5 pr-3 text-xs text-[color:var(--muted)] whitespace-nowrap">{fmtDate(row.created_at)}</td>
        <td className="py-2.5 pr-3 text-xs text-[color:var(--muted)] whitespace-nowrap">{fmtDate(row.updated_at)}</td>
        <td className="py-2.5 pr-3 text-sm text-right tabular-nums" data-testid={`shortlink-clicks-${row.video_id}`}>
          {(row.clicks || 0).toLocaleString()}
        </td>
        <td className="py-2.5 whitespace-nowrap">
          <button onClick={copy}
                  className={`p-1.5 rounded hover:bg-white/10 ${copied ? "text-emerald-300" : "text-[color:var(--muted)] hover:text-[color:var(--accent)]"}`}
                  title="Copy short link" data-testid={`shortlink-copy-${row.video_id}`}>
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <a href={`${API}/shortlinks/${row.slug}/qr.png`} download={`storylo-${row.slug}-qr.png`}
             target="_blank" rel="noopener noreferrer"
             className="inline-block p-1.5 rounded hover:bg-white/10 text-[color:var(--muted)] hover:text-[color:var(--accent)]"
             title="Download QR code" data-testid={`shortlink-qr-${row.video_id}`}>
            <QrCode className="w-3.5 h-3.5" />
          </a>
          <ShareRow url={shortUrl} title={row.title} />
          <button onClick={() => setExpanded((e) => !e)}
                  className="p-1.5 rounded hover:bg-white/10 text-[color:var(--muted)] hover:text-white"
                  title="Click analytics" data-testid={`shortlink-expand-${row.video_id}`}>
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-white/5 bg-white/[.02]" data-testid={`shortlink-analytics-${row.video_id}`}>
          <td colSpan={7} className="py-3 px-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
              <div>
                <div className="label-eyebrow mb-1 text-[10px]">Last clicked</div>
                <div className="text-[color:var(--muted)]">{row.last_clicked_at ? new Date(row.last_clicked_at).toLocaleString("en-GB") : "Never"}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-1 text-[10px]">Referrers</div>
                {topEntries(row.referrers).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 text-[color:var(--muted)]"><span className="truncate">{k.replace(/_/g, ".")}</span><span>{v}</span></div>
                ))}
                {topEntries(row.referrers).length === 0 && <div className="text-[color:var(--muted)]">—</div>}
              </div>
              <div>
                <div className="label-eyebrow mb-1 text-[10px]">Devices</div>
                {topEntries(row.devices).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 text-[color:var(--muted)]"><span>{k}</span><span>{v}</span></div>
                ))}
                {topEntries(row.devices).length === 0 && <div className="text-[color:var(--muted)]">—</div>}
              </div>
              <div>
                <div className="label-eyebrow mb-1 text-[10px]">Countries</div>
                {topEntries(row.countries).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 text-[color:var(--muted)]"><span>{flag(k)} {k}</span><span>{v}</span></div>
                ))}
                {topEntries(row.countries).length === 0 && <div className="text-[color:var(--muted)]">—</div>}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function ShortLinksPanel() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [refreshState, setRefreshState] = useState(null);

  const pollRefresh = async () => {
    try {
      const { data: st } = await axios.get(`${API}/admin/endcard-refresh/status`, {
        headers: { "X-Admin-Token": token() },
      });
      setRefreshState(st);
      if (st.running) setTimeout(pollRefresh, 8000);
    } catch { /* ignore */ }
  };

  const startEndcardRefresh = async () => {
    try {
      const { data: st } = await axios.post(`${API}/admin/endcard-refresh/start`, {}, {
        headers: { "X-Admin-Token": token() },
      });
      setRefreshState(st);
      toast.success(st.already_running
        ? "End-card refresh is already running"
        : "Started — every video will quietly re-render with its short URL on the end-card");
      setTimeout(pollRefresh, 4000);
    } catch { toast.error("Couldn't start the end-card refresh"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/shortlinks`, {
        headers: { "X-Admin-Token": token() },
      });
      setData(res);
    } catch { toast.error("Couldn't load short links"); }
    finally { setLoading(false); }
  };

  const backfill = async () => {
    try {
      const { data: res } = await axios.post(`${API}/shortlinks/backfill`, {}, {
        headers: { "X-Admin-Token": token() },
      });
      toast.success(res.created ? `Created ${res.created} new short link(s)` : "All stories already have short links");
      load();
    } catch { toast.error("Backfill failed"); }
  };

  const rows = (data?.links || []).filter((r) =>
    !filter ||
    (r.title || "").toLowerCase().includes(filter.toLowerCase()) ||
    (r.slug || "").includes(filter.toLowerCase()));

  return (
    <>
      <button
        onClick={() => { setOpen(true); load(); }}
        className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-white/10 text-[color:var(--muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5"
        data-testid="shortlinks-open"
        title="Every story's short promotional URL — copy, edit, QR codes and click analytics"
      >
        <Link2 className="w-3.5 h-3.5" /> Short Links
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="shortlinks-modal"
             onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="panel max-w-5xl w-full max-h-[88vh] flex flex-col p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-eyebrow mb-1">Short Links</div>
                <div className="text-sm text-[color:var(--muted)]">
                  Clean, memorable URLs for promos, QR codes and social — every story gets one automatically.
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[color:var(--muted)] hover:text-white"
                      data-testid="shortlinks-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search title or slug…"
                className="flex-1 bg-white/[.05] border border-white/10 rounded-lg px-3 py-2 text-sm placeholder:text-[color:var(--muted)] focus:outline-none focus:border-[color:var(--accent)]"
                data-testid="shortlinks-search"
              />
              <button onClick={backfill}
                      className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 rounded-md border border-white/10 text-[color:var(--muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
                      data-testid="shortlinks-backfill">
                Generate missing
              </button>
              <button onClick={startEndcardRefresh}
                      disabled={refreshState?.running}
                      className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 rounded-md border border-[color:var(--accent)]/40 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 disabled:opacity-50"
                      data-testid="shortlinks-endcard-refresh"
                      title="Quietly re-render every published video so its end-card shows 'Watch the full documentary — storylo.co.uk/…'. Runs one video at a time; stories stay live throughout.">
                Stamp URLs on all videos
              </button>
            </div>
            {refreshState && (refreshState.running || refreshState.done > 0) && (
              <div className="mb-3 text-xs text-[color:var(--muted)] flex items-center gap-2"
                   data-testid="endcard-refresh-progress">
                {refreshState.running && <Loader2 className="w-3.5 h-3.5 animate-spin text-[color:var(--accent)]" />}
                {refreshState.running
                  ? <>Re-rendering end-cards: {refreshState.done}/{refreshState.total}
                      {refreshState.current ? <> — <span className="text-white truncate max-w-[240px] inline-block align-bottom">{refreshState.current}</span></> : null}
                      {refreshState.skipped > 0 ? ` (${refreshState.skipped} skipped)` : ""}</>
                  : <>End-card refresh finished: {refreshState.done} updated{refreshState.skipped > 0 ? `, ${refreshState.skipped} skipped (no source/busy)` : ""}</>}
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--muted)]" /></div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted)] border-b border-white/10">
                      <th className="py-2 pr-3">Story</th>
                      <th className="py-2 pr-3">Short URL</th>
                      <th className="py-2 pr-3 text-center">Status</th>
                      <th className="py-2 pr-3">Created</th>
                      <th className="py-2 pr-3">Updated</th>
                      <th className="py-2 pr-3 text-right">Clicks</th>
                      <th className="py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <Row key={r.video_id} row={r} base={data?.base || window.location.host} onSaved={load} />
                    ))}
                  </tbody>
                </table>
              )}
              {!loading && rows.length === 0 && (
                <div className="py-12 text-center text-sm text-[color:var(--muted)]" data-testid="shortlinks-empty">
                  No short links yet — click "Generate missing" to create them for every published story.
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
